import type { BookingTheme } from "@openings/template-components";

/**
 * Micro Precision is the production default; every other key is a skin.
 * The Oct 2026 cleanup removed the flat density/color presets (Compact,
 * Crisp, Legible, Measured, Navy, Warm) and the legacy Quiet Editorial:
 * an unknown or retired key resolves to Micro.
 */
export const BOOKING_PRECISION_VARIANT_KEYS = [
  "micro-precision",
  "soft-precision",
  "hacker",
  "atelier",
  "noir",
  "blossom",
  "sage",
  "sunset",
  "pop",
  "aurora",
] as const;

export type BookingPrecisionVariantKey =
  (typeof BOOKING_PRECISION_VARIANT_KEYS)[number];

export const DEFAULT_BOOKING_PRECISION_VARIANT: BookingPrecisionVariantKey =
  "micro-precision";

export const BOOKING_VISUAL_VARIANT_KEYS = [
  ...BOOKING_PRECISION_VARIANT_KEYS,
] as const;

export type BookingVisualVariantKey =
  (typeof BOOKING_VISUAL_VARIANT_KEYS)[number];

export const DEFAULT_BOOKING_VISUAL_VARIANT: BookingVisualVariantKey =
  DEFAULT_BOOKING_PRECISION_VARIANT;

const SYSTEM_SANS =
  'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
/** Installed on macOS/iOS (Iowan, Palatino) and Windows (Palatino Linotype). */
const SYSTEM_SERIF =
  '"Iowan Old Style", "Palatino Linotype", Palatino, "Book Antiqua", Georgia, serif';
/** SF Pro Rounded on Apple platforms; a plain system sans elsewhere. */
const SYSTEM_ROUNDED =
  'ui-rounded, "SF Pro Rounded", system-ui, -apple-system, "Segoe UI", sans-serif';

/**
 * Styling only — colors, sizes, weights, spacing, and CSS value strings.
 * Skins must never carry user-facing copy: labels like "Choose a time" are
 * the booking page's text, not a skin axis, and no token may change what
 * words render. Two hard rules protect the registry (see
 * `subdomain-booking-page.instructions.md` § skins):
 * 1. Adding or editing a preset must not change any other preset — the
 *    snapshot test locks every resolved token set.
 * 2. A new CSS var consumed by shared components must fall back to the
 *    exact value that was previously hard-coded there.
 */
export type BookingVisualVariantTokens = {
  canvasColor: string;
  /**
   * Full CSS background for the page canvas. Usually equal to
   * `canvasColor`; a skin may use a gradient here. Keep `canvasColor` a
   * plain color regardless — it remains the fallback and feeds Ant.
   */
  canvasBackground: string;
  /**
   * CSS `color-scheme` for the page and its portals. UA-rendered
   * internals (scrollbars, textarea resize grips, native form chrome)
   * follow this, not the tokens — a dark skin must set `"dark"` or those
   * pieces render for a light page. `"light"` on every flat preset.
   */
  colorScheme: string;
  surfaceColor: string;
  surfaceHoverColor: string;
  borderColor: string;
  dividerColor: string;
  textColor: string;
  textSecondaryColor: string;
  accentColor: string;
  accentTextColor: string;
  availabilityColor: "#22c55e";
  bodyFontFamily: string;
  headingFontFamily: string;
  brandFontSize: number;
  brandFontSizeStacked: number;
  brandFontWeight: number;
  cardTitleFontSize: number;
  cardTitleFontWeight: number;
  locationFontSize: number;
  sectionFontSize: number;
  /**
   * Section-heading ("Choose a service" / "Choose a time") treatment. The
   * flat presets render sentence-case semibold headings in the text color;
   * a skin may turn them into eyebrow labels — small uppercase, wide
   * letter-spacing, muted color. `sectionTextColor` follows `textColor`
   * unless set explicitly.
   */
  sectionFontWeight: number;
  sectionTextTransform: string;
  sectionLetterSpacing: string;
  sectionTextColor: string;
  nameFontSize: number;
  metaFontSize: number;
  profileTitleFontSize: number;
  profileTitleFontSizeStacked: number;
  outerPadding: number;
  outerPaddingStacked: number;
  cardPadding: number;
  cardPaddingStacked: number;
  majorSpacing: number;
  groupSpacing: number;
  panelRadius: number;
  controlRadius: number;
  controlHeight: number;
  controlLabelGap: number;
  timeButtonHeight: number;
  timeButtonPaddingX: number;
  timeButtonFontSize: number;
  /** Slot label weight. 500 on flat presets; Soft Precision uses 600. */
  timeButtonFontWeight: number;
  /** Gap between slots in the time grid. 8 on flat presets. */
  timeGridGap: number;
  avatarSize: number;
  /**
   * Muted chip surface: the initials avatar disk and the info-panel icon
   * buttons. Pinned `#F5F5F5` on every flat preset; a dark skin must
   * override it or those chips stay light.
   */
  mutedChipColor: string;
  timeButtonRadius: number;
  /** Card/panel box-shadow. `"none"` for the flat Precision presets. */
  surfaceShadow: string;
  /**
   * Resting border for controls inside the booking center (slots, selects,
   * date input). The B&W page spec pins this at `#E5E5E5` — the booking
   * center re-declares `--openings-color-border`, so the general
   * `borderColor` axis deliberately does NOT reach these controls. A skin
   * may soften it.
   */
  controlBorderColor: string;
  /**
   * Hover/open border for select-style controls. Follows `accentColor`
   * unless a preset sets it explicitly (a skin may prefer a translucent
   * border over the full accent).
   */
  controlHoverBorderColor: string;
  /** Hover border for time-slot buttons. `#CCCCCC` on the flat presets. */
  timeButtonHoverBorderColor: string;
  /**
   * Chosen-slot treatment. The flat presets fill the slot with the accent
   * (`background`/`border` follow `accentColor`, text follows
   * `accentTextColor`); a skin may outline it instead — translucent wash,
   * strong border, dark text, and a ring via `timeButtonSelectedShadow`.
   */
  timeButtonSelectedBackground: string;
  timeButtonSelectedBorderColor: string;
  timeButtonSelectedTextColor: string;
  timeButtonSelectedShadow: string;
};

/**
 * The canonical style groups. Every token belongs to exactly ONE group;
 * this map is the machine-checked table of contents for the skin system.
 * A new token axis MUST be added to a group here — the compile-time
 * assertion below fails on any unassigned key, and the completeness test
 * in `bookingVisualVariants.test.ts` rejects duplicates.
 *
 * The full reference — what pixels each group paints, which components
 * consume it, its floors and follow rules — lives in
 * `subdomain-booking-page.instructions.md` § Skin style groups. Read it
 * before adding a skin or an axis.
 */
export const BOOKING_TOKEN_GROUPS = {
  /** Page background and UA rendering mode. */
  canvas: ["canvasColor", "canvasBackground", "colorScheme"],
  /**
   * Cards and panels: fill, hover fill, outline, hairlines, radius, depth,
   * and the muted chip (avatar disk, info-panel icon buttons).
   */
  surface: [
    "surfaceColor",
    "surfaceHoverColor",
    "borderColor",
    "dividerColor",
    "panelRadius",
    "surfaceShadow",
    "mutedChipColor",
  ],
  /** Global ink: primary and secondary text color. */
  text: ["textColor", "textSecondaryColor"],
  /** Primary action color and the text that sits on it. */
  accent: ["accentColor", "accentTextColor"],
  /** Semantic constants a skin can never restyle. */
  semantic: ["availabilityColor"],
  /** Font families for body and headings. */
  typography: ["bodyFontFamily", "headingFontFamily"],
  /** Identity type: brand, card titles, member-profile titles, location. */
  titles: [
    "brandFontSize",
    "brandFontSizeStacked",
    "brandFontWeight",
    "cardTitleFontSize",
    "cardTitleFontWeight",
    "profileTitleFontSize",
    "profileTitleFontSizeStacked",
    "locationFontSize",
  ],
  /** Section headings ("Choose a service" / "Choose a time"). */
  labels: [
    "sectionFontSize",
    "sectionFontWeight",
    "sectionTextTransform",
    "sectionLetterSpacing",
    "sectionTextColor",
  ],
  /** Running text: member names, prices, durations, meta lines. */
  body: ["nameFontSize", "metaFontSize"],
  /** Selects, date input, primary action button. */
  controls: [
    "controlHeight",
    "controlRadius",
    "controlBorderColor",
    "controlHoverBorderColor",
    "controlLabelGap",
  ],
  /** Slot pills: geometry, type, grid, hover, and chosen-slot treatment. */
  timeSlots: [
    "timeButtonHeight",
    "timeButtonPaddingX",
    "timeButtonFontSize",
    "timeButtonFontWeight",
    "timeButtonRadius",
    "timeGridGap",
    "timeButtonHoverBorderColor",
    "timeButtonSelectedBackground",
    "timeButtonSelectedBorderColor",
    "timeButtonSelectedTextColor",
    "timeButtonSelectedShadow",
  ],
  /** Member avatars. */
  people: ["avatarSize"],
  /** Page and card rhythm: paddings and the two spacing steps. */
  spacing: [
    "outerPadding",
    "outerPaddingStacked",
    "cardPadding",
    "cardPaddingStacked",
    "majorSpacing",
    "groupSpacing",
  ],
} as const satisfies Record<
  string,
  readonly (keyof BookingVisualVariantTokens)[]
>;

export type BookingTokenGroupName = keyof typeof BOOKING_TOKEN_GROUPS;

type GroupedTokenKey =
  (typeof BOOKING_TOKEN_GROUPS)[BookingTokenGroupName][number];

/**
 * Compile-time completeness: if a token is added to
 * `BookingVisualVariantTokens` without being assigned to a group above,
 * this constant's type becomes the missing key name(s) and the assignment
 * fails to typecheck.
 */
const _everyTokenHasAGroup: Exclude<
  keyof BookingVisualVariantTokens,
  GroupedTokenKey
> extends never
  ? true
  : Exclude<keyof BookingVisualVariantTokens, GroupedTokenKey> = true;
void _everyTokenHasAGroup;

/**
 * One soft light in a skin's ambient layer: a radial glow of `color` that
 * fades from `opacity` at its center to transparent.
 */
export type BookingSkinGlow = {
  /** `#RRGGBB`. */
  color: string;
  /**
   * Peak opacity, 0–1. Text on the canvas must stay at 4.5:1 with every
   * glow stacked at its peak (`bookingVisualVariants.test.ts`).
   */
  opacity: number;
  /**
   * CSS `<position>` of the center on the layer, e.g. `"21% 18%"`. The
   * layer overhangs the viewport by 20% on every side, so viewport point
   * `v` is layer `(v + 20%) / 1.4`.
   */
  at: string;
  /** Ellipse radii on the layer, e.g. `"45% 35%"`. */
  size: string;
};

/**
 * Motion a skin may add (Nov 2026). It is not a token: a preset without it
 * emits none of its CSS, so Micro and the static skins render exactly as
 * before. Only `transform` animates — composited, cheap on phones — never
 * color, so the readability E2E never reads a mid-transition color. Both
 * parts stop under `prefers-reduced-motion: reduce`.
 */
export type BookingSkinMotion = {
  /**
   * Glows on a fixed layer behind the page that drift slowly; one pass
   * takes `seconds`, then it drifts back.
   */
  ambient?: { glows: readonly BookingSkinGlow[]; seconds: number };
  /**
   * Pressable feedback on time slots and primary buttons: a hover moves by
   * `hoverTransform` and casts `hoverShadow`, a press sinks, and a newly
   * chosen slot pops.
   */
  press?: { hoverTransform: string; hoverShadow: string };
};

export type BookingVisualVariantDefinition = {
  label: string;
  description: string;
  tokens: BookingVisualVariantTokens;
  theme: BookingTheme;
  /**
   * A skin restyles surfaces the flat presets leave at their original
   * values (the portaled calendar). Only skins opt those surfaces into the
   * token palette, so adding a skin cannot change an existing preset.
   */
  skin: boolean;
  /** Skins only; see `BookingSkinMotion`. */
  motion?: BookingSkinMotion;
};

export type CompactPrecisionTokenOverrides = Omit<
  Partial<BookingVisualVariantTokens>,
  "availabilityColor"
> & {
  availabilityColor?: never;
};

function toBookingTheme(
  tokens: BookingVisualVariantTokens,
): BookingTheme {
  return {
    colorSurface: tokens.surfaceColor,
    colorBackground: tokens.canvasColor,
    colorBorder: tokens.borderColor,
    colorText: tokens.textColor,
    colorTextSecondary: tokens.textSecondaryColor,
    colorPrimary: tokens.accentColor,
    colorPrimaryText: tokens.accentTextColor,
    borderRadius: tokens.panelRadius,
    borderRadiusSm: tokens.controlRadius,
    borderWidth: 1,
    fontSizeLg: tokens.locationFontSize,
    fontSizeBase: tokens.nameFontSize,
    fontSizeSm: tokens.metaFontSize,
    fontSizeXs: tokens.metaFontSize,
    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightSemibold: 600,
    spacingXs: 4,
    spacingSm: 8,
    spacingMd: 12,
    spacingLg: tokens.majorSpacing,
    spacingXl: tokens.groupSpacing,
    avatarSize: tokens.avatarSize,
    avatarSizeSm: 36,
  };
}

function defineVariant(
  label: string,
  description: string,
  tokens: BookingVisualVariantTokens,
): BookingVisualVariantDefinition {
  return {
    label,
    description,
    tokens,
    theme: toBookingTheme(tokens),
    skin: false,
  };
}

function asSkin(
  definition: BookingVisualVariantDefinition,
  motion?: BookingSkinMotion,
): BookingVisualVariantDefinition {
  return motion
    ? { ...definition, skin: true, motion }
    : { ...definition, skin: true };
}

export const COMPACT_PRECISION_TOKENS: BookingVisualVariantTokens = {
  canvasColor: "#F6F6F6",
  canvasBackground: "#F6F6F6",
  colorScheme: "light",
  surfaceColor: "#FFFFFF",
  surfaceHoverColor: "#F2F2F2",
  borderColor: "#D6D6D6",
  dividerColor: "#EAEAEA",
  textColor: "#101010",
  textSecondaryColor: "#606060",
  accentColor: "#202020",
  accentTextColor: "#FFFFFF",
  availabilityColor: "#22c55e",
  bodyFontFamily: SYSTEM_SANS,
  headingFontFamily: SYSTEM_SANS,
  brandFontSize: 24,
  brandFontSizeStacked: 22,
  brandFontWeight: 700,
  cardTitleFontSize: 20,
  cardTitleFontWeight: 700,
  locationFontSize: 16,
  sectionFontSize: 15,
  sectionFontWeight: 600,
  sectionTextTransform: "none",
  sectionLetterSpacing: "normal",
  sectionTextColor: "#101010",
  nameFontSize: 15,
  metaFontSize: 13,
  profileTitleFontSize: 22,
  profileTitleFontSizeStacked: 19,
  outerPadding: 28,
  outerPaddingStacked: 16,
  cardPadding: 24,
  cardPaddingStacked: 16,
  majorSpacing: 20,
  groupSpacing: 24,
  panelRadius: 6,
  controlRadius: 6,
  controlHeight: 46,
  controlLabelGap: 8,
  timeButtonHeight: 40,
  timeButtonPaddingX: 12,
  timeButtonFontSize: 13,
  timeButtonFontWeight: 500,
  timeGridGap: 8,
  avatarSize: 40,
  mutedChipColor: "#F5F5F5",
  timeButtonRadius: 6,
  surfaceShadow: "none",
  controlBorderColor: "#E5E5E5",
  controlHoverBorderColor: "#202020",
  timeButtonHoverBorderColor: "#CCCCCC",
  timeButtonSelectedBackground: "#202020",
  timeButtonSelectedBorderColor: "#202020",
  timeButtonSelectedTextColor: "#FFFFFF",
  timeButtonSelectedShadow: "none",
};

export function deriveCompactPrecisionTokens(
  overrides: CompactPrecisionTokenOverrides,
): BookingVisualVariantTokens {
  const tokens = {
    ...COMPACT_PRECISION_TOKENS,
    ...overrides,
    availabilityColor: COMPACT_PRECISION_TOKENS.availabilityColor,
  };
  // canvasBackground follows canvasColor unless a preset sets it explicitly
  // (e.g. a gradient skin). Without this, overriding canvasColor alone would
  // silently leave the base background behind.
  if (overrides.canvasColor !== undefined &&
      overrides.canvasBackground === undefined) {
    tokens.canvasBackground = overrides.canvasColor;
  }
  // The accent-filled interaction defaults follow accentColor the same way:
  // a skin that recolors its accent keeps its filled chosen slot and control
  // hover in that accent without restating them.
  if (overrides.accentColor !== undefined) {
    if (overrides.controlHoverBorderColor === undefined) {
      tokens.controlHoverBorderColor = overrides.accentColor;
    }
    if (overrides.timeButtonSelectedBackground === undefined) {
      tokens.timeButtonSelectedBackground = overrides.accentColor;
    }
    if (overrides.timeButtonSelectedBorderColor === undefined) {
      tokens.timeButtonSelectedBorderColor = overrides.accentColor;
    }
  }
  if (overrides.accentTextColor !== undefined &&
      overrides.timeButtonSelectedTextColor === undefined) {
    tokens.timeButtonSelectedTextColor = overrides.accentTextColor;
  }
  // Section headings render in the text color unless a skin mutes them.
  if (overrides.textColor !== undefined &&
      overrides.sectionTextColor === undefined) {
    tokens.sectionTextColor = overrides.textColor;
  }
  return tokens;
}

function defineCompactPrecisionVariant(
  label: string,
  description: string,
  overrides: CompactPrecisionTokenOverrides,
): BookingVisualVariantDefinition {
  return defineVariant(
    label,
    description,
    deriveCompactPrecisionTokens(overrides),
  );
}

export const BOOKING_VISUAL_VARIANTS: Record<
  BookingVisualVariantKey,
  BookingVisualVariantDefinition
> = {
  "micro-precision": defineCompactPrecisionVariant(
    "Micro Precision",
    "Smaller type and a tighter rhythm preserve density without reducing touch targets.",
    {
      brandFontSize: 22,
      brandFontSizeStacked: 20,
      cardTitleFontSize: 18,
      locationFontSize: 15,
      sectionFontSize: 14,
      nameFontSize: 14,
      profileTitleFontSize: 20,
      profileTitleFontSizeStacked: 18,
      outerPadding: 24,
      cardPadding: 20,
      majorSpacing: 16,
      groupSpacing: 20,
      controlHeight: 44,
    },
  ),
  "soft-precision": asSkin(defineCompactPrecisionVariant(
    "Soft Precision",
    "A soft card treatment for the production booking flow: translucent borders, generous radii, a gentle card shadow, a tinted gradient canvas, eyebrow section labels, dense slot pills, and unified pressable feedback — hovers darken the border with a faint wash, and the chosen slot is outlined, not filled.",
    {
      // Micro Precision framing (brand, titles, outer rhythm)...
      brandFontSize: 22,
      brandFontSizeStacked: 20,
      cardTitleFontSize: 18,
      locationFontSize: 15,
      profileTitleFontSize: 20,
      profileTitleFontSizeStacked: 18,
      outerPadding: 24,
      majorSpacing: 16,
      groupSpacing: 20,
      // ...at the compact card control scale. Slot pills sit on the 40px
      // time-target floor; form controls and primary actions keep the 44px
      // floor (booking-flow.instructions.md § target sizes).
      cardPadding: 18,
      controlHeight: 44,
      controlLabelGap: 6,
      nameFontSize: 13,
      metaFontSize: 12,
      timeButtonFontSize: 12,
      timeButtonFontWeight: 600,
      timeButtonPaddingX: 6,
      timeGridGap: 6,
      // Soft eyebrow labels: small uppercase, wide tracking, muted — but
      // still 4.5:1 on the surface, since they name functional steps.
      sectionFontSize: 11,
      sectionFontWeight: 700,
      sectionTextTransform: "uppercase",
      sectionLetterSpacing: "0.08em",
      sectionTextColor: "rgba(12, 12, 15, 0.6)",
      // The soft-card palette and depth. Accessibility floors (40px time
      // targets, 16px stacked padding) are inherited from the base, not restated.
      canvasColor: "#F4F4F8",
      canvasBackground:
        "linear-gradient(175deg, #FFFFFF 0%, #F7F7FA 55%, #F0F0F6 100%)",
      surfaceHoverColor: "rgba(12, 12, 15, 0.03)",
      borderColor: "rgba(12, 12, 15, 0.1)",
      dividerColor: "rgba(12, 12, 15, 0.08)",
      textColor: "#0C0C0F",
      textSecondaryColor: "rgba(12, 12, 15, 0.6)",
      accentColor: "#0C0C0F",
      panelRadius: 18,
      controlRadius: 12,
      timeButtonRadius: 8,
      surfaceShadow:
        "0 1px 2px rgba(11, 11, 18, 0.04), 0 12px 28px rgba(11, 11, 18, 0.06)",
      // Every pressable surface uses the same hover treatment (0.1 border
      // darkens to 0.45 over a 0.03 wash), and the chosen slot is outlined
      // with a soft ring instead of accent-filled.
      controlBorderColor: "rgba(12, 12, 15, 0.1)",
      controlHoverBorderColor: "rgba(12, 12, 15, 0.45)",
      timeButtonHoverBorderColor: "rgba(12, 12, 15, 0.45)",
      timeButtonSelectedBackground: "rgba(12, 12, 15, 0.04)",
      timeButtonSelectedBorderColor: "rgba(12, 12, 15, 0.8)",
      timeButtonSelectedTextColor: "#0C0C0F",
      timeButtonSelectedShadow: "0 0 0 2px rgba(12, 12, 15, 0.08)",
    },
  )),
  hacker: asSkin(defineCompactPrecisionVariant(
    "Hacker",
    "Green-on-black terminal: monospace type, sharp corners, phosphor ink, a faint CRT glow, and inverse-video selection. The first dark skin, and proof the token system carries a full palette flip.",
    {
      // Phosphor palette. canvasColor stays a plain hex for fallbacks/Ant;
      // the canvas itself gets a subtle CRT vignette.
      canvasColor: "#060A06",
      canvasBackground:
        "radial-gradient(ellipse 120% 80% at 50% 0%, #0B140B 0%, #060A06 55%, #040704 100%)",
      // UA internals (scrollbars, resize grips, native chrome) go dark.
      colorScheme: "dark",
      surfaceColor: "#0A100A",
      surfaceHoverColor: "rgba(51, 255, 102, 0.08)",
      borderColor: "rgba(51, 255, 102, 0.28)",
      dividerColor: "rgba(51, 255, 102, 0.14)",
      textColor: "#33FF66",
      textSecondaryColor: "rgba(51, 255, 102, 0.55)",
      // Inverse video: green fill, near-black text. Deliberately NOT the
      // semantic availability green (#22c55e) — the guard test enforces it.
      accentColor: "#33FF66",
      accentTextColor: "#041008",
      bodyFontFamily:
        '"SF Mono", ui-monospace, "Cascadia Code", Menlo, Consolas, monospace',
      headingFontFamily:
        '"SF Mono", ui-monospace, "Cascadia Code", Menlo, Consolas, monospace',
      // Terminal geometry: everything squares off.
      panelRadius: 0,
      controlRadius: 0,
      timeButtonRadius: 0,
      // Prompt-style section labels in dim phosphor.
      sectionFontSize: 12,
      sectionTextTransform: "uppercase",
      sectionLetterSpacing: "0.08em",
      sectionTextColor: "rgba(51, 255, 102, 0.55)",
      // Depth is glow, not shadow.
      surfaceShadow:
        "0 0 0 1px rgba(51, 255, 102, 0.12), 0 0 24px rgba(51, 255, 102, 0.07)",
      mutedChipColor: "rgba(51, 255, 102, 0.12)",
      controlBorderColor: "rgba(51, 255, 102, 0.35)",
      timeButtonHoverBorderColor: "rgba(51, 255, 102, 0.8)",
      timeButtonSelectedShadow: "0 0 0 2px rgba(51, 255, 102, 0.25)",
      // controlHoverBorderColor and the selected slot's fill/border/text
      // follow accentColor/accentTextColor via the deriver: full-green
      // hover, green fill, near-black text.
    },
  )),
  atelier: asSkin(defineCompactPrecisionVariant(
    "Atelier",
    "Editorial salon: warm ivory paper, serif headings, espresso ink, hairline rules, and near-square corners. Quiet luxury, flat by design.",
    {
      // Micro framing with editorial type: serif for identity and titles,
      // the system sans for running text and controls.
      headingFontFamily: SYSTEM_SERIF,
      brandFontSize: 26,
      brandFontSizeStacked: 23,
      brandFontWeight: 600,
      cardTitleFontSize: 21,
      cardTitleFontWeight: 600,
      profileTitleFontSize: 22,
      profileTitleFontSizeStacked: 20,
      locationFontSize: 15,
      nameFontSize: 14,
      outerPadding: 28,
      cardPadding: 24,
      majorSpacing: 18,
      groupSpacing: 24,
      controlHeight: 44,
      // Small-caps style labels: tracked uppercase in the secondary ink.
      sectionFontSize: 11,
      sectionFontWeight: 600,
      sectionTextTransform: "uppercase",
      sectionLetterSpacing: "0.16em",
      sectionTextColor: "#6B5D51",
      // Warm paper palette. The canvas stays flat: an editorial page, not a
      // gradient.
      canvasColor: "#F6F1E9",
      surfaceColor: "#FFFDF9",
      surfaceHoverColor: "rgba(59, 42, 30, 0.04)",
      borderColor: "rgba(59, 42, 30, 0.16)",
      dividerColor: "rgba(59, 42, 30, 0.10)",
      textColor: "#2A211A",
      textSecondaryColor: "#6B5D51",
      accentColor: "#3B2A1E",
      accentTextColor: "#FFFDF9",
      panelRadius: 2,
      controlRadius: 2,
      timeButtonRadius: 2,
      mutedChipColor: "#F1EAE0",
      controlBorderColor: "rgba(59, 42, 30, 0.2)",
      timeButtonHoverBorderColor: "rgba(59, 42, 30, 0.55)",
      // Filled espresso chosen slot and accent hover follow accentColor via
      // the deriver.
    },
  )),
  noir: asSkin(defineCompactPrecisionVariant(
    "Noir",
    "Evening barbershop: charcoal and warm black, champagne-gold accents, serif headings, deep shadows, and gold-filled selection. The second dark skin.",
    {
      headingFontFamily: SYSTEM_SERIF,
      brandFontSize: 26,
      brandFontSizeStacked: 23,
      brandFontWeight: 600,
      cardTitleFontSize: 20,
      cardTitleFontWeight: 600,
      profileTitleFontSize: 22,
      profileTitleFontSizeStacked: 20,
      locationFontSize: 15,
      nameFontSize: 14,
      outerPadding: 24,
      cardPadding: 22,
      majorSpacing: 18,
      groupSpacing: 22,
      controlHeight: 44,
      // Gold, tracked labels.
      sectionFontSize: 11,
      sectionFontWeight: 600,
      sectionTextTransform: "uppercase",
      sectionLetterSpacing: "0.18em",
      sectionTextColor: "#CDAE72",
      canvasColor: "#0E0D0B",
      canvasBackground:
        "radial-gradient(ellipse 110% 70% at 50% 0%, #1C1915 0%, #0E0D0B 60%, #0A0908 100%)",
      colorScheme: "dark",
      surfaceColor: "#171512",
      surfaceHoverColor: "rgba(205, 174, 114, 0.08)",
      borderColor: "rgba(240, 230, 212, 0.14)",
      dividerColor: "rgba(240, 230, 212, 0.09)",
      textColor: "#F2ECE2",
      textSecondaryColor: "rgba(242, 236, 226, 0.66)",
      // Champagne gold, deliberately not the availability green.
      accentColor: "#CDAE72",
      accentTextColor: "#16120B",
      panelRadius: 4,
      controlRadius: 4,
      timeButtonRadius: 4,
      surfaceShadow:
        "inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 18px 40px rgba(0, 0, 0, 0.45)",
      mutedChipColor: "rgba(242, 236, 226, 0.08)",
      controlBorderColor: "rgba(242, 236, 226, 0.2)",
      timeButtonHoverBorderColor: "rgba(205, 174, 114, 0.75)",
      timeButtonSelectedShadow: "0 0 0 2px rgba(205, 174, 114, 0.22)",
      // Gold-filled chosen slot with dark text, and the gold control hover,
      // follow accentColor/accentTextColor via the deriver.
    },
  )),
  blossom: asSkin(defineCompactPrecisionVariant(
    "Blossom",
    "Friendly studio: blush gradient canvas, rounded type, pill-shaped slots and controls, raspberry accents, and a soft-filled chosen slot.",
    {
      bodyFontFamily: SYSTEM_ROUNDED,
      headingFontFamily: SYSTEM_ROUNDED,
      brandFontSize: 24,
      brandFontSizeStacked: 22,
      brandFontWeight: 800,
      cardTitleFontSize: 19,
      cardTitleFontWeight: 800,
      profileTitleFontSize: 21,
      profileTitleFontSizeStacked: 19,
      locationFontSize: 15,
      nameFontSize: 14,
      outerPadding: 24,
      cardPadding: 22,
      majorSpacing: 16,
      groupSpacing: 20,
      controlHeight: 44,
      // Sentence-case labels in a deep raspberry.
      sectionFontSize: 14,
      sectionFontWeight: 700,
      sectionTextColor: "#8E2F57",
      canvasColor: "#FDF1F4",
      canvasBackground:
        "linear-gradient(160deg, #FFF8F9 0%, #FDF0F4 55%, #FBE6EE 100%)",
      surfaceHoverColor: "rgba(184, 59, 110, 0.05)",
      borderColor: "rgba(150, 40, 90, 0.12)",
      dividerColor: "rgba(150, 40, 90, 0.08)",
      textColor: "#3A1F2B",
      textSecondaryColor: "#765262",
      accentColor: "#B83B6E",
      accentTextColor: "#FFFFFF",
      // Everything pressable is a pill; cards are generously rounded.
      panelRadius: 24,
      controlRadius: 22,
      timeButtonRadius: 20,
      surfaceShadow:
        "0 2px 4px rgba(150, 40, 90, 0.04), 0 16px 32px rgba(150, 40, 90, 0.08)",
      mutedChipColor: "#FCE8EF",
      controlBorderColor: "rgba(150, 40, 90, 0.16)",
      timeButtonFontWeight: 600,
      timeButtonHoverBorderColor: "rgba(184, 59, 110, 0.5)",
      // The chosen slot is soft-filled, not solid: blush wash, raspberry
      // border and ink, and a light ring.
      timeButtonSelectedBackground: "#FCE4EE",
      timeButtonSelectedBorderColor: "#B83B6E",
      timeButtonSelectedTextColor: "#7E2449",
      timeButtonSelectedShadow: "0 0 0 3px rgba(184, 59, 110, 0.15)",
    },
  )),
  sage: asSkin(defineCompactPrecisionVariant(
    "Sage",
    "Calm spa: a pale sage canvas, soft white cards, forest-green accents, tracked labels, and a soft-filled chosen slot. Static by design.",
    {
      brandFontSize: 23,
      brandFontSizeStacked: 21,
      brandFontWeight: 600,
      cardTitleFontSize: 19,
      cardTitleFontWeight: 600,
      profileTitleFontSize: 21,
      profileTitleFontSizeStacked: 19,
      locationFontSize: 15,
      nameFontSize: 14,
      outerPadding: 26,
      cardPadding: 22,
      majorSpacing: 18,
      groupSpacing: 22,
      controlHeight: 44,
      sectionFontSize: 12,
      sectionFontWeight: 600,
      sectionTextTransform: "uppercase",
      sectionLetterSpacing: "0.12em",
      sectionTextColor: "#4E6553",
      canvasColor: "#E6EEE2",
      canvasBackground:
        "linear-gradient(180deg, #EDF3EA 0%, #E6EEE2 50%, #DDE8D8 100%)",
      surfaceColor: "#FAFCF8",
      surfaceHoverColor: "rgba(63, 91, 71, 0.05)",
      borderColor: "rgba(47, 74, 56, 0.14)",
      dividerColor: "rgba(47, 74, 56, 0.09)",
      textColor: "#1F2A22",
      textSecondaryColor: "#56635A",
      accentColor: "#3F5B47",
      accentTextColor: "#FFFFFF",
      panelRadius: 14,
      controlRadius: 10,
      timeButtonRadius: 10,
      surfaceShadow:
        "0 1px 2px rgba(31, 42, 34, 0.04), 0 10px 24px rgba(31, 42, 34, 0.05)",
      mutedChipColor: "#DCE6D7",
      controlBorderColor: "rgba(47, 74, 56, 0.2)",
      timeButtonHoverBorderColor: "rgba(63, 91, 71, 0.5)",
      // Soft-filled chosen slot: sage wash, forest border and ink, a ring.
      timeButtonSelectedBackground: "#E3EBDF",
      timeButtonSelectedBorderColor: "#3F5B47",
      timeButtonSelectedTextColor: "#22352A",
      timeButtonSelectedShadow: "0 0 0 3px rgba(63, 91, 71, 0.12)",
    },
  )),
  sunset: asSkin(
    defineCompactPrecisionVariant(
      "Sunset",
      "Golden hour: peach, coral and amber light drifting behind the page, rounded type, pill controls, and burnt-orange accents. The first moving skin.",
      {
        bodyFontFamily: SYSTEM_ROUNDED,
        headingFontFamily: SYSTEM_ROUNDED,
        brandFontSize: 24,
        brandFontSizeStacked: 22,
        brandFontWeight: 800,
        cardTitleFontSize: 19,
        cardTitleFontWeight: 800,
        profileTitleFontSize: 21,
        profileTitleFontSizeStacked: 19,
        locationFontSize: 15,
        nameFontSize: 14,
        outerPadding: 24,
        cardPadding: 22,
        majorSpacing: 16,
        groupSpacing: 20,
        controlHeight: 44,
        sectionFontSize: 12,
        sectionFontWeight: 800,
        sectionTextTransform: "uppercase",
        sectionLetterSpacing: "0.1em",
        sectionTextColor: "#A3360C",
        canvasColor: "#FFF1E8",
        canvasBackground:
          "linear-gradient(165deg, #FFF6EE 0%, #FFEDE3 50%, #FFE6E6 100%)",
        surfaceHoverColor: "rgba(194, 65, 12, 0.05)",
        borderColor: "rgba(140, 60, 20, 0.12)",
        dividerColor: "rgba(140, 60, 20, 0.08)",
        textColor: "#2B1A12",
        textSecondaryColor: "#6E4535",
        accentColor: "#C2410C",
        accentTextColor: "#FFFFFF",
        // Pills: each control radius is half its height.
        panelRadius: 22,
        controlRadius: 22,
        timeButtonRadius: 20,
        surfaceShadow:
          "0 2px 6px rgba(194, 65, 12, 0.06), 0 18px 40px rgba(194, 65, 12, 0.1)",
        mutedChipColor: "#FFE8DA",
        controlBorderColor: "rgba(140, 60, 20, 0.18)",
        timeButtonFontWeight: 700,
        timeButtonHoverBorderColor: "rgba(194, 65, 12, 0.55)",
        // Orange-filled chosen slot with white text follows the accent; it
        // glows instead of carrying a ring.
        timeButtonSelectedShadow: "0 6px 16px rgba(194, 65, 12, 0.28)",
      },
    ),
    {
      ambient: {
        seconds: 32,
        glows: [
          { color: "#FFB38A", opacity: 0.55, at: "21% 18%", size: "45% 35%" },
          { color: "#FF8FAB", opacity: 0.4, at: "79% 32%", size: "40% 35%" },
          { color: "#FFD27A", opacity: 0.45, at: "50% 82%", size: "55% 30%" },
        ],
      },
      press: {
        hoverTransform: "translateY(-2px)",
        hoverShadow: "0 8px 18px rgba(194, 65, 12, 0.18)",
      },
    },
  ),
  pop: asSkin(
    defineCompactPrecisionVariant(
      "Pop",
      "Bold and playful: a butter dot-grid canvas, black outlines, hard offset shadows, cobalt actions, and a yellow chosen slot. Slots lift on hover and press in.",
      {
        brandFontSize: 26,
        brandFontSizeStacked: 23,
        brandFontWeight: 900,
        cardTitleFontSize: 21,
        cardTitleFontWeight: 900,
        profileTitleFontSize: 23,
        profileTitleFontSizeStacked: 20,
        locationFontSize: 15,
        nameFontSize: 14,
        outerPadding: 24,
        cardPadding: 22,
        majorSpacing: 18,
        groupSpacing: 22,
        controlHeight: 44,
        sectionFontSize: 12,
        sectionFontWeight: 800,
        sectionTextTransform: "uppercase",
        sectionLetterSpacing: "0.08em",
        // A dot grid over butter; canvasColor stays the plain butter.
        canvasColor: "#FFF4CC",
        canvasBackground:
          "radial-gradient(circle at 1px 1px, rgba(17, 17, 17, 0.14) 1px, transparent 1.5px) 0 0 / 18px 18px, #FFF4CC",
        surfaceHoverColor: "#FFF4CC",
        borderColor: "#111111",
        dividerColor: "rgba(17, 17, 17, 0.14)",
        textColor: "#111111",
        textSecondaryColor: "#474747",
        accentColor: "#2742F5",
        accentTextColor: "#FFFFFF",
        panelRadius: 14,
        controlRadius: 10,
        timeButtonRadius: 10,
        // Depth is a hard offset shadow, not a blur.
        surfaceShadow: "4px 4px 0 #111111",
        mutedChipColor: "#FFE36E",
        controlBorderColor: "#111111",
        timeButtonFontWeight: 700,
        timeButtonHoverBorderColor: "#111111",
        timeButtonSelectedBackground: "#FFE14D",
        timeButtonSelectedBorderColor: "#111111",
        timeButtonSelectedTextColor: "#111111",
        timeButtonSelectedShadow: "2px 2px 0 #111111",
      },
    ),
    {
      press: {
        hoverTransform: "translate(-2px, -2px)",
        hoverShadow: "3px 3px 0 #111111",
      },
    },
  ),
  aurora: asSkin(
    defineCompactPrecisionVariant(
      "Aurora",
      "Night sky: teal and violet northern lights drifting behind midnight cards, lavender accents, and a glowing chosen slot. The third dark skin, and it moves.",
      {
        brandFontSize: 24,
        brandFontSizeStacked: 22,
        brandFontWeight: 700,
        cardTitleFontSize: 19,
        cardTitleFontWeight: 700,
        profileTitleFontSize: 21,
        profileTitleFontSizeStacked: 19,
        locationFontSize: 15,
        nameFontSize: 14,
        outerPadding: 24,
        cardPadding: 22,
        majorSpacing: 18,
        groupSpacing: 22,
        controlHeight: 44,
        sectionFontSize: 11,
        sectionFontWeight: 700,
        sectionTextTransform: "uppercase",
        sectionLetterSpacing: "0.14em",
        sectionTextColor: "#7DE3D3",
        canvasColor: "#0A0F1F",
        canvasBackground:
          "linear-gradient(180deg, #0D1328 0%, #0A0F1F 55%, #080B17 100%)",
        colorScheme: "dark",
        surfaceColor: "#121A30",
        surfaceHoverColor: "rgba(165, 180, 252, 0.08)",
        borderColor: "rgba(199, 210, 254, 0.14)",
        dividerColor: "rgba(199, 210, 254, 0.09)",
        textColor: "#EEF1FF",
        textSecondaryColor: "rgba(238, 241, 255, 0.72)",
        // Lavender, deliberately not the availability green.
        accentColor: "#A5B4FC",
        accentTextColor: "#0A0F1F",
        panelRadius: 16,
        controlRadius: 10,
        timeButtonRadius: 10,
        surfaceShadow:
          "inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 24px 60px rgba(2, 6, 23, 0.55)",
        mutedChipColor: "rgba(199, 210, 254, 0.1)",
        controlBorderColor: "rgba(199, 210, 254, 0.22)",
        timeButtonHoverBorderColor: "rgba(165, 180, 252, 0.7)",
        // Lavender-filled chosen slot with dark text follows the accent.
        timeButtonSelectedShadow:
          "0 0 0 3px rgba(165, 180, 252, 0.2), 0 0 22px rgba(165, 180, 252, 0.45)",
      },
    ),
    {
      ambient: {
        seconds: 28,
        glows: [
          { color: "#2DD4BF", opacity: 0.2, at: "25% 18%", size: "45% 30%" },
          { color: "#8B5CF6", opacity: 0.22, at: "75% 29%", size: "40% 35%" },
          { color: "#3B82F6", opacity: 0.14, at: "50% 82%", size: "55% 30%" },
        ],
      },
      press: {
        hoverTransform: "translateY(-2px)",
        hoverShadow:
          "0 10px 24px rgba(2, 6, 23, 0.5), 0 0 0 1px rgba(165, 180, 252, 0.3)",
      },
    },
  ),
};

export function isBookingPrecisionVariantKey(
  value: unknown,
): value is BookingPrecisionVariantKey {
  return (
    typeof value === "string" &&
    (BOOKING_PRECISION_VARIANT_KEYS as readonly string[]).includes(value)
  );
}

export function resolveBookingPrecisionVariantKey(
  value?: string | null,
): BookingPrecisionVariantKey {
  return isBookingPrecisionVariantKey(value)
    ? value
    : DEFAULT_BOOKING_PRECISION_VARIANT;
}

export function isBookingVisualVariantKey(
  value: unknown,
): value is BookingVisualVariantKey {
  return (
    typeof value === "string" &&
    (BOOKING_VISUAL_VARIANT_KEYS as readonly string[]).includes(value)
  );
}

export function resolveBookingVisualVariantKey(
  value?: string | null,
): BookingVisualVariantKey {
  return isBookingVisualVariantKey(value)
    ? value
    : DEFAULT_BOOKING_VISUAL_VARIANT;
}

/**
 * The look every booking surface of a business renders: the owner's
 * choice on Settings → Website (`Salon.bookingSkin`), or Micro when it is
 * unset, unknown, or a retired skin.
 */
export function resolveSalonVisualVariant(salon: {
  bookingSkin?: string | null;
}): BookingVisualVariantKey {
  return resolveBookingVisualVariantKey(salon.bookingSkin);
}

export function resolveBookingVisualVariant(
  value?: string | null,
): BookingVisualVariantDefinition {
  return BOOKING_VISUAL_VARIANTS[resolveBookingVisualVariantKey(value)];
}
